/**
 * MEVIA v5.0 - D11: Dynamic Company Onboarding
 * 
 * Builds onboarding steps dynamically based on subscription plan.
 * Step types: BASE (always), CONDITIONAL_REQUIRED, CONDITIONAL_OPTIONAL
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import {
  CheckCircle2, Circle, ChevronRight, ChevronLeft, Building2,
  Calendar, Target, ClipboardList, Settings2, Users, Sparkles,
  AlertTriangle, Info, Loader2, Save, BarChart3, CheckCheck,
  Clock, FileText, Shield, Activity, Lock, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import logger from '../utils/logger';


// Icon mapping for step icons
const ICON_MAP = {
  Building2, Calendar, BarChart3, ClipboardList, Users,
  FileText, Shield, Settings, Activity, Settings2, CheckCheck
};

// ============== TRANSLATIONS ==============
const TEXTS = {
  es: {
    title: 'Configuracion Inicial',
    step: 'Paso',
    of: 'de',
    next: 'Siguiente',
    prev: 'Anterior',
    complete: 'Completar',
    configureLater: 'Configurar despues',
    skip: 'Omitir (datos ya configurados)',
    loading: 'Cargando...',
    saving: 'Guardando...',
    requiredSteps: 'pasos requeridos completados',
    activateCycle: 'Activar Ciclo',
    // Step 1 (B1)
    step1Info: 'Para continuar, necesitas tener configurado al menos 1 area y 1 puesto.',
    step1Complete: 'Ya tienes areas y puestos configurados',
    step1Incomplete: 'Necesitas configurar al menos 1 area y 1 puesto',
    // Step 2 (B2)
    cycleName: 'Nombre del Ciclo',
    cycleNamePlaceholder: 'Ej: Ciclo 2026',
    cycleDuration: 'Duracion del Ciclo',
    annual: 'Anual (12 meses)',
    semiannual: 'Semestral (6 meses)',
    evalFrequency: 'Frecuencia de Revisiones',
    monthly: 'Mensual',
    bimonthly: 'Bimestral',
    quarterly: 'Trimestral',
    startDate: 'Fecha de Inicio',
    evalDeadline: 'Dias limite para evaluaciones',
    // Step C1
    profileInstructions: 'Evalua tu empresa TAL COMO ES HOY, no como quisieras que fuera.',
    profileNotes: 'Notas adicionales (opcional)',
    // Step C2
    questionnaireTime: 'Tiempo estimado: 15 minutos',
    currentProfile: 'Perfil Actual',
    objectiveProfile: 'Perfil Objetivo',
    adjustValues: 'Ajustar Valores',
    justification: 'Justificacion de ajustes',
    realismWarning: 'Un cambio de mas de 30 puntos es poco realista.',
    confirmAsAdmin: 'Confirmar como Administrador',
    confirmAsCEO: 'Confirmar como CEO',
    // Step C3
    jobProfilesInfo: 'Asigna un perfil cultural a cada puesto. Puedes hacerlo despues desde Configuracion.',
    // Step M1 (Modules)
    modulesDesc: 'Selecciona que modulos estaran activos para este ciclo',
    moduleRequired: 'Requerido',
    notIncluded: 'No incluido',
    planNotice: 'Los modulos con "No incluido" no estan en tu plan actual.',
    // Step X (Final)
    reviewSummary: 'Resumen de configuracion',
    pendingOptional: 'Pasos opcionales pendientes',
    completeNow: 'Completar ahora',
    orgStructure: 'Estructura organizacional',
    evaluationCycle: 'Ciclo de evaluacion',
    companyProfile: 'Perfil de empresa',
    configured: 'Configurado',
    pending: 'Pendiente',
    skipped: 'Omitido',
    // N1
    n1RazonSocial: 'Razon Social',
    n1RFC: 'RFC',
    n1IMSS: 'Registro IMSS (opcional)',
    n1RepLegal: 'Representante Legal',
    n1Responsable: 'Responsable NOM-035',
    n1FechaInicio: 'Fecha inicio ciclo NOM-035',
    // N2
    n2Generate: 'Generar borrador de politica',
    n2Approve: 'Aprobar politica',
    n2PolicyLabel: 'Texto de la politica',
    // N3
    n3GuidesLabel: 'Guias de Referencia',
    n3DeadlineLabel: 'Fecha limite de aplicacion',
    n3AnonymousLabel: 'Cuestionarios anonimos',
    n3SuggestedGuides: 'Guias sugeridas segun tamano',
    // P1
    p1DayLabel: 'Dia del mes para envio',
    p1ChannelLabel: 'Canal de notificacion',
    p1Email: 'Correo electronico',
    p1Platform: 'Plataforma',
    p1Both: 'Ambos',
    // Success
    onboardingComplete: 'Configuracion completada!',
    onboardingCompleteDesc: 'Tu empresa esta lista para comenzar',
    goToDashboard: 'Ir al Dashboard',
    // Modules
    dimensions: 'Evaluacion de Dimensiones',
    dimensionsDesc: 'Evalua los 10 Meta-Capitales',
    enneagram: 'Evaluacion de Eneagrama',
    enneagramDesc: 'Perfil de personalidad',
    nom035: 'NOM-035',
    nom035Desc: 'Cumplimiento normativo mexicano',
    pulse: 'Encuestas de Pulso',
    pulseDesc: 'Encuestas breves y frecuentes',
    dashboard: 'Dashboard Central',
    dashboardDesc: 'Vista consolidada de indicadores',
    projector: 'Proyector de Escenarios',
    projectorDesc: 'Simulacion de cambios',
    // Capitals
    FISICO: 'Capital Fisico', HUMANO: 'Capital Humano',
    MANUFACTURADO: 'Capital Manufacturado', FINANCIERO: 'Capital Financiero',
    NATURAL: 'Capital Natural', CULTURAL: 'Capital Cultural',
    SOCIAL: 'Capital Social', CONOCIMIENTO: 'Capital de Conocimiento',
    PSICOLOGICO: 'Capital Psicologico', PROPOSITO: 'Capital de Proposito'
  },
  en: {
    title: 'Initial Setup',
    step: 'Step',
    of: 'of',
    next: 'Next',
    prev: 'Previous',
    complete: 'Complete',
    configureLater: 'Configure later',
    skip: 'Skip (data already configured)',
    loading: 'Loading...',
    saving: 'Saving...',
    requiredSteps: 'required steps completed',
    activateCycle: 'Activate Cycle',
    step1Info: 'To continue, you need at least 1 area and 1 position configured.',
    step1Complete: 'You already have areas and positions configured',
    step1Incomplete: 'You need to configure at least 1 area and 1 position',
    cycleName: 'Cycle Name',
    cycleNamePlaceholder: 'E.g.: Cycle 2026',
    cycleDuration: 'Cycle Duration',
    annual: 'Annual (12 months)',
    semiannual: 'Semiannual (6 months)',
    evalFrequency: 'Review Frequency',
    monthly: 'Monthly',
    bimonthly: 'Bimonthly',
    quarterly: 'Quarterly',
    startDate: 'Start Date',
    evalDeadline: 'Deadline days for evaluations',
    profileInstructions: 'Evaluate your company AS IT IS TODAY, not as you wish it were.',
    profileNotes: 'Additional notes (optional)',
    questionnaireTime: 'Estimated time: 15 minutes',
    currentProfile: 'Current Profile',
    objectiveProfile: 'Objective Profile',
    adjustValues: 'Adjust Values',
    justification: 'Justification for adjustments',
    realismWarning: 'A change of more than 30 points is unrealistic.',
    confirmAsAdmin: 'Confirm as Administrator',
    confirmAsCEO: 'Confirm as CEO',
    jobProfilesInfo: 'Assign a cultural profile to each position. You can do this later from Settings.',
    modulesDesc: 'Select which modules will be active for this cycle',
    moduleRequired: 'Required',
    notIncluded: 'Not included',
    planNotice: 'Modules marked "Not included" are not in your current plan.',
    reviewSummary: 'Configuration Summary',
    pendingOptional: 'Pending optional steps',
    completeNow: 'Complete now',
    orgStructure: 'Organizational structure',
    evaluationCycle: 'Evaluation cycle',
    companyProfile: 'Company profile',
    configured: 'Configured',
    pending: 'Pending',
    skipped: 'Skipped',
    n1RazonSocial: 'Business Name',
    n1RFC: 'RFC (Tax ID)',
    n1IMSS: 'IMSS Registration (optional)',
    n1RepLegal: 'Legal Representative',
    n1Responsable: 'NOM-035 Responsible',
    n1FechaInicio: 'NOM-035 cycle start date',
    n2Generate: 'Generate policy draft',
    n2Approve: 'Approve policy',
    n2PolicyLabel: 'Policy text',
    n3GuidesLabel: 'Reference Guides',
    n3DeadlineLabel: 'Application deadline',
    n3AnonymousLabel: 'Anonymous questionnaires',
    n3SuggestedGuides: 'Suggested guides by company size',
    p1DayLabel: 'Day of month for sending',
    p1ChannelLabel: 'Notification channel',
    p1Email: 'Email',
    p1Platform: 'Platform',
    p1Both: 'Both',
    onboardingComplete: 'Setup completed!',
    onboardingCompleteDesc: 'Your company is ready to start',
    goToDashboard: 'Go to Dashboard',
    dimensions: 'Dimensions Evaluation',
    dimensionsDesc: 'Evaluate the 10 Meta-Capitals',
    enneagram: 'Enneagram Evaluation',
    enneagramDesc: 'Personality profile',
    nom035: 'NOM-035',
    nom035Desc: 'Mexican regulatory compliance',
    pulse: 'Pulse Surveys',
    pulseDesc: 'Brief and frequent surveys',
    dashboard: 'Central Dashboard',
    dashboardDesc: 'Consolidated indicator view',
    projector: 'Scenario Projector',
    projectorDesc: 'Change simulation',
    FISICO: 'Physical Capital', HUMANO: 'Human Capital',
    MANUFACTURADO: 'Manufactured Capital', FINANCIERO: 'Financial Capital',
    NATURAL: 'Natural Capital', CULTURAL: 'Cultural Capital',
    SOCIAL: 'Social Capital', CONOCIMIENTO: 'Knowledge Capital',
    PSICOLOGICO: 'Psychological Capital', PROPOSITO: 'Purpose Capital'
  }
};

const META_CAPITALS = [
  'FISICO', 'HUMANO', 'MANUFACTURADO', 'FINANCIERO', 'NATURAL',
  'CULTURAL', 'SOCIAL', 'CONOCIMIENTO', 'PSICOLOGICO', 'PROPOSITO'
];

// ============== STEP INDICATOR ==============
const StepIndicator = ({ steps, currentStepIndex, t }) => (
  <div className="flex items-center justify-center flex-wrap gap-1 mb-6" data-testid="step-indicator">
    {steps.map((step, index) => {
      const isCompleted = step.status === 'completed';
      const isSkipped = step.status === 'skipped';
      const isCurrent = index === currentStepIndex;

      return (
        <React.Fragment key={step.id}>
          <div
            data-testid={`step-dot-${step.id}`}
            className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all text-xs font-medium
              ${isCompleted ? 'bg-emerald-500 border-emerald-500 text-white' :
                isSkipped ? 'bg-amber-100 border-amber-400 text-amber-700' :
                isCurrent ? 'bg-blue-500 border-blue-500 text-white' :
                'bg-gray-100 border-gray-300 text-gray-500'}`}
            title={step.title}
          >
            {isCompleted ? <CheckCircle2 className="w-4 h-4" /> :
             isSkipped ? <Clock className="w-4 h-4" /> :
             step.id}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-4 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ============== MAIN COMPONENT ==============
export default function CompanyOnboarding({ empresaId, onComplete }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { permissions: subPerms } = useSubscription();
  const t = TEXTS[language] || TEXTS.es;
  const lang = language || 'es';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [pasosCompletados, setPasosCompletados] = useState({});
  const [progressInfo, setProgressInfo] = useState({});
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Step-specific state
  const [cycleConfig, setCycleConfig] = useState({
    nombre_ciclo: `Ciclo ${new Date().getFullYear()}`,
    duracion_tipo: 'ANUAL',
    frecuencia_evaluaciones: 'TRIMESTRAL',
    fecha_inicio: new Date().toISOString().split('T')[0],
    dias_limite_evaluaciones: 45
  });
  const [currentProfile, setCurrentProfile] = useState(() => {
    const init = {};
    META_CAPITALS.forEach(c => { init[c] = 50; });
    return init;
  });
  const [profileNotes, setProfileNotes] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [objectivePreview, setObjectivePreview] = useState(null);
  const [adjustedProfile, setAdjustedProfile] = useState({});
  const [adjustMode, setAdjustMode] = useState(false);
  const [justification, setJustification] = useState('');
  const [c2Phase, setC2Phase] = useState('questionnaire'); // 'questionnaire' or 'review'
  const [modules, setModules] = useState({
    dimensiones: true, eneagrama: true, nom035: false,
    pulso: true, dashboard_central: true, proyector_escenarios: true
  });
  // N1 state
  const [n1Data, setN1Data] = useState({
    razon_social: '', rfc: '', registro_imss: '',
    representante_legal: '', responsable_nom035: '', fecha_inicio_ciclo: ''
  });
  // N2 state
  const [n2PolicyText, setN2PolicyText] = useState('');
  const [n2Approved, setN2Approved] = useState(false);
  // N3 state
  const [n3Config, setN3Config] = useState({ guias_referencia: [], fecha_limite_aplicacion: '', es_anonimo: true });
  const [n3SuggestedGuides, setN3SuggestedGuides] = useState([]);
  const [n3EmpCount, setN3EmpCount] = useState(0);
  // P1 state
  const [p1Config, setP1Config] = useState({ dia_envio: 1, canal_notificacion: 'both' });

  // ============== LOAD DYNAMIC STEPS ==============
  const loadDynamicSteps = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/v5/empresa/${empresaId}/onboarding/dynamic-steps`);
      if (response.data.success) {
        const data = response.data.data;
        setSteps(data.steps);
        setPasosCompletados(data.pasos_completados);
        setProgressInfo(data.progress);
        setOnboardingComplete(data.onboarding_complete);

        // Find first incomplete required step
        const firstPending = data.steps.findIndex(s => s.status === 'pending');
        if (firstPending >= 0) {
          setCurrentStepIndex(firstPending);
        } else {
          setCurrentStepIndex(data.steps.length - 1);
        }
      }
    } catch (error) {
      logger.error('Error loading dynamic steps:', error);
      toast.error(lang === 'es' ? 'Error al cargar pasos' : 'Error loading steps');
    } finally {
      setLoading(false);
    }
  }, [empresaId, lang]);

  useEffect(() => {
    if (empresaId) loadDynamicSteps();
  }, [empresaId, loadDynamicSteps]);

  // Load step-specific data when step changes
  const currentStep = steps[currentStepIndex];
  const currentStepId = currentStep?.id;

  useEffect(() => {
    if (!currentStepId) return;
    if (currentStepId === 'C2' && c2Phase === 'questionnaire') loadQuestions();
    if (currentStepId === 'C2' && c2Phase === 'review') loadStep5Preview();
    if (currentStepId === 'N1') loadN1Data();
    if (currentStepId === 'N2') loadN2Data();
    if (currentStepId === 'N3') loadN3Data();
  }, [currentStepId, c2Phase]);

  // ============== DATA LOADERS ==============
  const loadQuestions = async () => {
    try {
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step4/questions?lang=${lang}`);
      if (res.data.success) setQuestions(res.data.data.questions || []);
    } catch (e) { logger.error('Error loading questions:', e); }
  };

  const loadStep5Preview = async () => {
    try {
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step5/preview`);
      if (res.data.success) {
        setObjectivePreview(res.data.data);
        setAdjustedProfile(res.data.data.perfil_objetivo || {});
      }
    } catch (e) { logger.error('Error loading preview:', e); }
  };

  const loadN1Data = async () => {
    try {
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step/N1/data`);
      if (res.data.success && res.data.data) setN1Data(res.data.data);
    } catch (e) { /* table may not exist yet */ }
  };

  const loadN2Data = async () => {
    try {
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step/N2/data`);
      if (res.data.success && res.data.data) {
        setN2PolicyText(res.data.data.policy_text || '');
        setN2Approved(res.data.data.approved || false);
      }
    } catch (e) { /* table may not exist yet */ }
  };

  const loadN3Data = async () => {
    try {
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step/N3/data`);
      if (res.data.success && res.data.data) {
        setN3SuggestedGuides(res.data.data.suggested_guides || []);
        setN3EmpCount(res.data.data.employee_count || 0);
        if (res.data.data.config) {
          setN3Config(res.data.data.config);
        } else {
          setN3Config(prev => ({ ...prev, guias_referencia: res.data.data.suggested_guides || [] }));
        }
      }
    } catch (e) { /* table may not exist yet */ }
  };

  // ============== STEP COMPLETION HANDLERS ==============
  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1);
  };

  const markStepComplete = (stepId) => {
    setPasosCompletados(prev => ({ ...prev, [stepId]: true }));
    setSteps(prev => prev.map(s => s.id === stepId ? { ...s, status: 'completed' } : s));
  };

  // B1: Catalogs & Structure
  const handleB1Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step1/complete`);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Estructura configurada' : 'Structure configured');
        markStepComplete('B1');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // B2: Evaluation Cycle
  const handleB2Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step2`, {
        ...cycleConfig, fecha_inicio: new Date(cycleConfig.fecha_inicio).toISOString()
      });
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Ciclo configurado' : 'Cycle configured');
        markStepComplete('B2');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // C1: Current Profile
  const handleC1Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `/v5/empresa/${empresaId}/onboarding/step3?confirmed_by=${user.id}`,
        { capital_values: currentProfile, notas: profileNotes }
      );
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Perfil guardado' : 'Profile saved');
        markStepComplete('C1');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // C2: Strategic Questionnaire → Review/Confirm (two phases)
  const handleC2QuestionnaireSubmit = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `/v5/empresa/${empresaId}/onboarding/step4?responded_by=${user.id}`,
        {
          reto_crecimiento: answers.reto_crecimiento || 'expansion_mercado',
          cultura_tradicional_innovadora: answers.cultura_tradicional_innovadora || 0,
          cultura_individual_colaborativa: answers.cultura_individual_colaborativa || 0,
          problema_rotacion: answers.problema_rotacion || 'primeros_6_meses',
          problema_rotacion_detalle: answers.problema_rotacion_detalle || null,
          capacidades_faltantes: answers.capacidades_faltantes || [],
          valoracion_bienestar: answers.valoracion_bienestar || 3,
          capitales_inversion: answers.capitales_inversion || [],
          tipo_liderazgo: answers.tipo_liderazgo || 'COACH',
          claridad_proposito: answers.claridad_proposito || 3,
          proposito_texto: answers.proposito_texto || null
        }
      );
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Cuestionario completado' : 'Questionnaire completed');
        setC2Phase('review');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleC2Adjust = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `/v5/empresa/${empresaId}/onboarding/step5/adjust`,
        { capital_values: adjustedProfile, justificacion: justification }
      );
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Ajustes aplicados' : 'Adjustments applied');
        setObjectivePreview(prev => ({ ...prev, perfil_objetivo: adjustedProfile, brechas: res.data.data.brechas }));
        setAdjustMode(false);
      } else { toast.error(res.data.message); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleC2Confirm = async (isCEO = false) => {
    try {
      setSaving(true);
      const res = await axios.post(
        `/v5/empresa/${empresaId}/onboarding/step5/confirm?approved_by=${user.id}&is_ceo=${isCEO}`
      );
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Perfil Objetivo confirmado' : 'Objective Profile confirmed');
        markStepComplete('C2');
        setC2Phase('questionnaire');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // C3: Job Profiles (optional - skip or mark done)
  const handleC3Skip = async () => {
    try {
      setSaving(true);
      await axios.post(`/v5/empresa/${empresaId}/onboarding/step/C3/skip`);
      toast.info(lang === 'es' ? 'Perfiles de puesto: configurar despues' : 'Job profiles: configure later');
      setPasosCompletados(prev => ({ ...prev, C3: 'skipped' }));
      setSteps(prev => prev.map(s => s.id === 'C3' ? { ...s, status: 'skipped' } : s));
      goToNextStep();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // N1: NOM-035 Workplace Data
  const handleN1Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step/N1/data`, n1Data);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Datos laborales guardados' : 'Workplace data saved');
        markStepComplete('N1');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // N2: Prevention Policy
  const handleN2GenerateDraft = async () => {
    try {
      setSaving(true);
      const res = await axios.get(`/v5/empresa/${empresaId}/onboarding/step/N2/generate?lang=${lang}`);
      if (res.data.success) {
        setN2PolicyText(res.data.data.draft_policy);
        toast.success(lang === 'es' ? 'Borrador generado' : 'Draft generated');
      }
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const handleN2Approve = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step/N2/data`, {
        policy_text: n2PolicyText, approved: true
      });
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Politica aprobada' : 'Policy approved');
        markStepComplete('N2');
        goToNextStep();
      }
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  // N3: NOM-035 Assessment Config
  const handleN3Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step/N3/data`, n3Config);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Config NOM-035 guardada' : 'NOM-035 config saved');
        markStepComplete('N3');
        goToNextStep();
      }
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  // P1: Pulse Config (optional)
  const handleP1Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step/P1/data`, p1Config);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Pulso configurado' : 'Pulse configured');
        markStepComplete('P1');
        goToNextStep();
      }
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const handleP1Skip = async () => {
    try {
      setSaving(true);
      await axios.post(`/v5/empresa/${empresaId}/onboarding/step/P1/skip`);
      toast.info(lang === 'es' ? 'Pulso: configurar despues' : 'Pulse: configure later');
      setPasosCompletados(prev => ({ ...prev, P1: 'skipped' }));
      setSteps(prev => prev.map(s => s.id === 'P1' ? { ...s, status: 'skipped' } : s));
      goToNextStep();
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  // M1: Active Modules
  const handleM1Complete = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`/v5/empresa/${empresaId}/onboarding/step6`, modules);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Modulos configurados' : 'Modules configured');
        markStepComplete('M1');
        goToNextStep();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // X: Final Activation
  const handleXActivate = async () => {
    try {
      setSaving(true);
      await axios.post(`/v5/empresa/${empresaId}/onboarding/step/X/complete`);
      toast.success(lang === 'es' ? 'Ciclo activado!' : 'Cycle activated!');
      markStepComplete('X');
      setOnboardingComplete(true);
      if (onComplete) onComplete();
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  // ============== QUESTION RENDERER (for C2) ==============
  const renderQuestionInput = (question) => {
    switch (question.type) {
      case 'single_choice':
        return (
          <RadioGroup value={answers[question.id] || ''} onValueChange={(v) => setAnswers(p => ({ ...p, [question.id]: v }))}>
            {question.options.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2 py-1">
                <RadioGroupItem value={opt.value} id={`${question.id}-${opt.value}`} />
                <Label htmlFor={`${question.id}-${opt.value}`} className="cursor-pointer">
                  {opt.label}
                  {opt.description && <span className="text-sm text-gray-500 ml-2">- {opt.description}</span>}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      case 'single_choice_with_text':
        return (
          <div className="space-y-3">
            <RadioGroup value={answers[question.id] || ''} onValueChange={(v) => setAnswers(p => ({ ...p, [question.id]: v }))}>
              {question.options.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`${question.id}-${opt.value}`} />
                  <Label htmlFor={`${question.id}-${opt.value}`}>{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
            {answers[question.id] === 'roles_especificos' && (
              <Input placeholder="Especifica los roles..." value={answers[`${question.id}_detalle`] || ''}
                onChange={(e) => setAnswers(p => ({ ...p, [`${question.id}_detalle`]: e.target.value }))} />
            )}
          </div>
        );
      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.max_selections && (
              <p className="text-sm text-gray-500">{(answers[question.id] || []).length} / {question.max_selections}</p>
            )}
            {question.options.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2">
                <Checkbox id={`${question.id}-${opt.value}`}
                  checked={(answers[question.id] || []).includes(opt.value)}
                  onCheckedChange={(checked) => {
                    const current = answers[question.id] || [];
                    if (checked) {
                      if (!question.max_selections || current.length < question.max_selections)
                        setAnswers(p => ({ ...p, [question.id]: [...current, opt.value] }));
                    } else {
                      setAnswers(p => ({ ...p, [question.id]: current.filter(v => v !== opt.value) }));
                    }
                  }} />
                <Label htmlFor={`${question.id}-${opt.value}`}>{opt.label}</Label>
              </div>
            ))}
          </div>
        );
      case 'dual_scale':
        return (
          <div className="space-y-4">
            {question.scales.map((scale) => (
              <div key={scale.id} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{scale.left}</span>
                  <span className="font-medium">{answers[scale.id] || 0}</span>
                  <span>{scale.right}</span>
                </div>
                <Slider value={[answers[scale.id] || 0]} onValueChange={([v]) => setAnswers(p => ({ ...p, [scale.id]: v }))}
                  min={scale.min} max={scale.max} step={1} />
              </div>
            ))}
          </div>
        );
      case 'scale':
        return (
          <div className="space-y-3">
            <Slider value={[answers[question.id] || question.min]}
              onValueChange={([v]) => setAnswers(p => ({ ...p, [question.id]: v }))}
              min={question.min} max={question.max} step={1} />
            <div className="grid grid-cols-5 gap-1 text-xs text-center">
              {Object.entries(question.labels || {}).map(([val, label]) => (
                <div key={val} className={answers[question.id] === parseInt(val) ? 'font-bold text-blue-600' : ''}>{label}</div>
              ))}
            </div>
          </div>
        );
      case 'scale_with_text':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{question.scale.label}</span>
                <span className="font-medium">{answers[`${question.id}_scale`] || question.scale.min}</span>
              </div>
              <Slider value={[answers[`${question.id}_scale`] || question.scale.min]}
                onValueChange={([v]) => setAnswers(p => ({ ...p, [`${question.id}_scale`]: v }))}
                min={question.scale.min} max={question.scale.max} step={1} />
            </div>
            <Textarea placeholder={question.text_placeholder} value={answers[`${question.id}_text`] || ''}
              onChange={(e) => setAnswers(p => ({ ...p, [`${question.id}_text`]: e.target.value }))} rows={3} />
          </div>
        );
      default:
        return null;
    }
  };

  // ============== STEP RENDERERS ==============
  const renderB1 = () => {
    const isComplete = pasosCompletados.B1 === true;
    return (
      <div className="space-y-6" data-testid="step-B1">
        <div className={`p-4 rounded-lg border-2 ${isComplete ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
          <div className="flex items-start gap-3">
            {isComplete ? <CheckCircle2 className="w-6 h-6 text-green-600 mt-1" /> : <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1" />}
            <div>
              <p className="font-medium">{isComplete ? t.step1Complete : t.step1Incomplete}</p>
              <p className="text-sm text-gray-600 mt-1">{t.step1Info}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="step-B1-next" onClick={handleB1Complete} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t.next} <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  const renderB2 = () => (
    <div className="space-y-6" data-testid="step-B2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t.cycleName}</Label>
          <Input value={cycleConfig.nombre_ciclo} onChange={(e) => setCycleConfig(p => ({ ...p, nombre_ciclo: e.target.value }))} placeholder={t.cycleNamePlaceholder} />
        </div>
        <div className="space-y-2">
          <Label>{t.startDate}</Label>
          <Input type="date" value={cycleConfig.fecha_inicio} onChange={(e) => setCycleConfig(p => ({ ...p, fecha_inicio: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t.cycleDuration}</Label>
          <RadioGroup value={cycleConfig.duracion_tipo} onValueChange={(v) => setCycleConfig(p => ({ ...p, duracion_tipo: v }))}>
            <div className="flex items-center space-x-2"><RadioGroupItem value="ANUAL" id="anual" /><Label htmlFor="anual">{t.annual}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="SEMESTRAL" id="semestral" /><Label htmlFor="semestral">{t.semiannual}</Label></div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>{t.evalFrequency}</Label>
          <RadioGroup value={cycleConfig.frecuencia_evaluaciones} onValueChange={(v) => setCycleConfig(p => ({ ...p, frecuencia_evaluaciones: v }))}>
            <div className="flex items-center space-x-2"><RadioGroupItem value="MENSUAL" id="mensual" /><Label htmlFor="mensual">{t.monthly}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="BIMESTRAL" id="bimestral" /><Label htmlFor="bimestral">{t.bimonthly}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="TRIMESTRAL" id="trimestral" /><Label htmlFor="trimestral">{t.quarterly}</Label></div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>{t.evalDeadline}: {cycleConfig.dias_limite_evaluaciones} {lang === 'es' ? 'dias' : 'days'}</Label>
          <Slider value={[cycleConfig.dias_limite_evaluaciones]} onValueChange={([v]) => setCycleConfig(p => ({ ...p, dias_limite_evaluaciones: v }))} min={15} max={90} step={5} />
        </div>
      </div>
      <NavButtons />
    </div>
  );

  const renderC1 = () => (
    <div className="space-y-6" data-testid="step-C1">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <p className="text-sm text-blue-800">{t.profileInstructions}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {META_CAPITALS.map((capital) => (
          <div key={capital} className="space-y-2">
            <div className="flex justify-between">
              <Label className="font-medium">{t[capital]}</Label>
              <span className="text-sm font-bold text-blue-600">{currentProfile[capital] || 50}</span>
            </div>
            <Slider value={[currentProfile[capital] || 50]} onValueChange={([v]) => setCurrentProfile(p => ({ ...p, [capital]: v }))}
              min={0} max={100} step={5} className="py-2" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Label>{t.profileNotes}</Label>
        <Textarea value={profileNotes} onChange={(e) => setProfileNotes(e.target.value)} rows={3} />
      </div>
      <NavButtons onNext={handleC1Complete} />
    </div>
  );

  const renderC2 = () => {
    if (c2Phase === 'review' || pasosCompletados.STEP_4_CUESTIONARIO) {
      return renderC2Review();
    }
    return renderC2Questionnaire();
  };

  const renderC2Questionnaire = () => (
    <div className="space-y-6" data-testid="step-C2-questionnaire">
      <div className="p-3 bg-gray-50 rounded-lg text-center">
        <p className="text-sm text-gray-600">{t.questionnaireTime}</p>
      </div>
      {questions.map((question, index) => (
        <Card key={question.id} className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">{index + 1}. {question.text}</CardTitle></CardHeader>
          <CardContent>{renderQuestionInput(question)}</CardContent>
        </Card>
      ))}
      <NavButtons onNext={handleC2QuestionnaireSubmit} />
    </div>
  );

  const renderC2Review = () => (
    <div className="space-y-6" data-testid="step-C2-review">
      {objectivePreview && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-lg">{t.currentProfile}</CardTitle></CardHeader>
              <CardContent>
                {META_CAPITALS.map((capital) => (
                  <div key={capital} className="flex justify-between py-1 border-b last:border-0">
                    <span className="text-sm">{t[capital]}</span>
                    <span className="font-medium">{objectivePreview.perfil_actual?.[capital] || 0}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">{t.objectiveProfile}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setAdjustMode(!adjustMode)}>{t.adjustValues}</Button>
                </div>
              </CardHeader>
              <CardContent>
                {META_CAPITALS.map((capital) => {
                  const current = objectivePreview.perfil_actual?.[capital] || 0;
                  const objective = adjustMode ? (adjustedProfile[capital] || 0) : (objectivePreview.perfil_objetivo?.[capital] || 0);
                  const gap = objective - current;
                  return (
                    <div key={capital} className="py-2 border-b last:border-0">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{t[capital]}</span>
                        <div className="flex items-center gap-3">
                          {adjustMode ? (
                            <Input type="number" min={0} max={100} value={adjustedProfile[capital] || 0}
                              onChange={(e) => setAdjustedProfile(p => ({ ...p, [capital]: parseInt(e.target.value) || 0 }))} className="w-20 h-8" />
                          ) : <span className="font-medium">{objective}</span>}
                          <Badge variant={gap > 15 ? 'destructive' : gap > 0 ? 'default' : 'secondary'}>{gap > 0 ? '+' : ''}{gap}</Badge>
                        </div>
                      </div>
                      {gap > 30 && <p className="text-xs text-red-500 mt-1"><AlertTriangle className="w-3 h-3 inline mr-1" />{t.realismWarning}</p>}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
          {adjustMode && (
            <div className="space-y-3">
              <Label>{t.justification}</Label>
              <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={2} />
              <Button onClick={handleC2Adjust} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}<Save className="w-4 h-4 mr-2" />{lang === 'es' ? 'Aplicar' : 'Apply'}
              </Button>
            </div>
          )}
        </>
      )}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setC2Phase('questionnaire')}><ChevronLeft className="w-4 h-4 mr-2" />{t.prev}</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleC2Confirm(false)} disabled={saving} data-testid="confirm-admin">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t.confirmAsAdmin}
          </Button>
          <Button onClick={() => handleC2Confirm(true)} disabled={saving} data-testid="confirm-ceo">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t.confirmAsCEO} <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderC3 = () => (
    <div className="space-y-6" data-testid="step-C3">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-600 mt-0.5" />
          <p className="text-sm text-blue-800">{t.jobProfilesInfo}</p>
        </div>
      </div>
      <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500">
        <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p className="font-medium">{lang === 'es' ? 'Perfiles de Puesto' : 'Job Profiles'}</p>
        <p className="text-sm mt-1">{lang === 'es' ? 'Esta funcion estara disponible proximamente desde Configuracion > Perfiles de Puesto' : 'This feature will be available soon from Settings > Job Profiles'}</p>
      </div>
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={goToPrevStep}><ChevronLeft className="w-4 h-4 mr-2" />{t.prev}</Button>
        <Button variant="outline" onClick={handleC3Skip} disabled={saving} data-testid="skip-C3">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Clock className="w-4 h-4 mr-2" />{t.configureLater}
        </Button>
      </div>
    </div>
  );

  const renderN1 = () => (
    <div className="space-y-6" data-testid="step-N1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t.n1RazonSocial} *</Label>
          <Input value={n1Data.razon_social} onChange={(e) => setN1Data(p => ({ ...p, razon_social: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t.n1RFC} *</Label>
          <Input value={n1Data.rfc} onChange={(e) => setN1Data(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} placeholder="XXXX000000XXX" maxLength={13} />
        </div>
        <div className="space-y-2">
          <Label>{t.n1IMSS}</Label>
          <Input value={n1Data.registro_imss} onChange={(e) => setN1Data(p => ({ ...p, registro_imss: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t.n1RepLegal} *</Label>
          <Input value={n1Data.representante_legal} onChange={(e) => setN1Data(p => ({ ...p, representante_legal: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t.n1Responsable} *</Label>
          <Input value={n1Data.responsable_nom035} onChange={(e) => setN1Data(p => ({ ...p, responsable_nom035: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>{t.n1FechaInicio}</Label>
          <Input type="date" value={n1Data.fecha_inicio_ciclo} onChange={(e) => setN1Data(p => ({ ...p, fecha_inicio_ciclo: e.target.value }))} />
        </div>
      </div>
      <NavButtons onNext={handleN1Complete} nextDisabled={!n1Data.razon_social || !n1Data.rfc || !n1Data.representante_legal || !n1Data.responsable_nom035} />
    </div>
  );

  const renderN2 = () => (
    <div className="space-y-6" data-testid="step-N2">
      <div className="flex gap-3">
        <Button variant="outline" onClick={handleN2GenerateDraft} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Sparkles className="w-4 h-4 mr-2" />{t.n2Generate}
        </Button>
      </div>
      <div className="space-y-2">
        <Label>{t.n2PolicyLabel}</Label>
        <Textarea value={n2PolicyText} onChange={(e) => setN2PolicyText(e.target.value)} rows={12} className="font-mono text-sm" />
      </div>
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={goToPrevStep}><ChevronLeft className="w-4 h-4 mr-2" />{t.prev}</Button>
        <Button onClick={handleN2Approve} disabled={saving || !n2PolicyText} data-testid="approve-n2">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Shield className="w-4 h-4 mr-2" />{t.n2Approve}
        </Button>
      </div>
    </div>
  );

  const renderN3 = () => (
    <div className="space-y-6" data-testid="step-N3">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="w-4 h-4 text-blue-600 inline mr-2" />
        <span className="text-sm text-blue-800">{t.n3SuggestedGuides}: {n3SuggestedGuides.join(', ')} ({n3EmpCount} {lang === 'es' ? 'empleados' : 'employees'})</span>
      </div>
      <div className="space-y-3">
        <Label>{t.n3GuidesLabel}</Label>
        {['I', 'II', 'III'].map(g => (
          <div key={g} className="flex items-center space-x-2">
            <Checkbox id={`guide-${g}`} checked={n3Config.guias_referencia.includes(g)}
              onCheckedChange={(checked) => {
                setN3Config(p => ({
                  ...p, guias_referencia: checked
                    ? [...p.guias_referencia, g]
                    : p.guias_referencia.filter(x => x !== g)
                }));
              }} />
            <Label htmlFor={`guide-${g}`}>{lang === 'es' ? `Guia de Referencia ${g}` : `Reference Guide ${g}`}</Label>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t.n3DeadlineLabel}</Label>
          <Input type="date" value={n3Config.fecha_limite_aplicacion || ''} onChange={(e) => setN3Config(p => ({ ...p, fecha_limite_aplicacion: e.target.value }))} />
        </div>
        <div className="flex items-center space-x-3 pt-6">
          <Switch checked={n3Config.es_anonimo} onCheckedChange={(v) => setN3Config(p => ({ ...p, es_anonimo: v }))} />
          <Label>{t.n3AnonymousLabel}</Label>
        </div>
      </div>
      <NavButtons onNext={handleN3Complete} nextDisabled={n3Config.guias_referencia.length === 0} />
    </div>
  );

  const renderP1 = () => (
    <div className="space-y-6" data-testid="step-P1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t.p1DayLabel}: {p1Config.dia_envio}</Label>
          <Slider value={[p1Config.dia_envio]} onValueChange={([v]) => setP1Config(p => ({ ...p, dia_envio: v }))} min={1} max={28} step={1} />
        </div>
        <div className="space-y-2">
          <Label>{t.p1ChannelLabel}</Label>
          <RadioGroup value={p1Config.canal_notificacion} onValueChange={(v) => setP1Config(p => ({ ...p, canal_notificacion: v }))}>
            <div className="flex items-center space-x-2"><RadioGroupItem value="email" id="ch-email" /><Label htmlFor="ch-email">{t.p1Email}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="platform" id="ch-plat" /><Label htmlFor="ch-plat">{t.p1Platform}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="both" id="ch-both" /><Label htmlFor="ch-both">{t.p1Both}</Label></div>
          </RadioGroup>
        </div>
      </div>
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={goToPrevStep}><ChevronLeft className="w-4 h-4 mr-2" />{t.prev}</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleP1Skip} disabled={saving} data-testid="skip-P1">
            <Clock className="w-4 h-4 mr-2" />{t.configureLater}
          </Button>
          <Button onClick={handleP1Complete} disabled={saving} data-testid="save-P1">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t.next} <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // D10: Plan ceiling for module toggles
  const planCeiling = subPerms?.plan_ceiling || {};
  const MODULE_PLAN_MAP = {
    dimensiones: 'culture', eneagrama: 'personality', nom035: 'nom035',
    pulso: 'pulse_surveys', dashboard_central: 'dashboard_central', proyector_escenarios: 'evolution_impact'
  };
  const isModuleLocked = (moduleKey) => {
    const planKey = MODULE_PLAN_MAP[moduleKey];
    return planKey ? planCeiling[planKey] === false : false;
  };

  const renderM1 = () => (
    <div className="space-y-6" data-testid="step-M1">
      <p className="text-sm text-gray-600">{t.modulesDesc}</p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">{t.planNotice}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { key: 'dimensiones', icon: BarChart3, color: 'text-blue-600', required: true },
          { key: 'eneagrama', icon: Users, color: 'text-purple-600' },
          { key: 'nom035', icon: ClipboardList, color: 'text-orange-600' },
          { key: 'pulso', icon: Sparkles, color: 'text-pink-600' },
          { key: 'dashboard_central', icon: Target, color: 'text-green-600' },
          { key: 'proyector_escenarios', icon: Settings2, color: 'text-indigo-600' }
        ].map(({ key, icon: Icon, color, required }) => {
          const locked = isModuleLocked(key);
          return (
            <Card key={key} className={`border-2 ${locked ? 'border-red-200 opacity-60' : required ? 'border-blue-500' : ''}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${color}`} />
                    <div>
                      <p className="font-medium">{t[key === 'pulso' ? 'pulse' : key === 'proyector_escenarios' ? 'projector' : key === 'dashboard_central' ? 'dashboard' : key === 'dimensiones' ? 'dimensions' : key === 'eneagrama' ? 'enneagram' : 'nom035']}</p>
                      <p className="text-sm text-gray-500">{t[key === 'pulso' ? 'pulseDesc' : key === 'proyector_escenarios' ? 'projectorDesc' : key === 'dashboard_central' ? 'dashboardDesc' : key === 'dimensiones' ? 'dimensionsDesc' : key === 'eneagrama' ? 'enneagramDesc' : 'nom035Desc']}</p>
                    </div>
                  </div>
                  {locked ? <Badge className="bg-red-100 text-red-600 border-red-200 text-xs"><Lock className="w-3 h-3 mr-1" />{t.notIncluded}</Badge> :
                   required ? <Badge className="bg-blue-500 text-white">{t.moduleRequired}</Badge> :
                   <Switch checked={modules[key]} onCheckedChange={(v) => setModules(p => ({ ...p, [key]: v }))} />}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <NavButtons onNext={handleM1Complete} />
    </div>
  );

  const renderX = () => {
    const skippedSteps = steps.filter(s => s.status === 'skipped');
    const completedRequired = steps.filter(s => s.required && s.status === 'completed').length;
    const totalRequired = steps.filter(s => s.required).length;
    const allRequiredDone = completedRequired >= totalRequired - 1; // -1 because X itself is not yet done

    return (
      <div className="space-y-6" data-testid="step-X">
        <h3 className="text-lg font-semibold">{t.reviewSummary}</h3>
        <div className="space-y-3">
          {steps.filter(s => s.id !== 'X').map(s => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {s.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> :
                 s.status === 'skipped' ? <Clock className="w-5 h-5 text-amber-500" /> :
                 <Circle className="w-5 h-5 text-gray-400" />}
                <span className="text-sm font-medium">{lang === 'es' ? s.title_es : s.title_en}</span>
                {!s.required && <Badge variant="outline" className="text-xs ml-2">{lang === 'es' ? 'Opcional' : 'Optional'}</Badge>}
              </div>
              <Badge className={s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : s.status === 'skipped' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>
                {s.status === 'completed' ? t.configured : s.status === 'skipped' ? t.skipped : t.pending}
              </Badge>
            </div>
          ))}
        </div>

        {skippedSteps.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">{t.pendingOptional}</p>
                <ul className="mt-2 space-y-1">
                  {skippedSteps.map(s => (
                    <li key={s.id} className="text-sm text-amber-700">
                      {lang === 'es' ? s.title_es : s.title_en}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={goToPrevStep}><ChevronLeft className="w-4 h-4 mr-2" />{t.prev}</Button>
          <Button onClick={handleXActivate} disabled={saving || !allRequiredDone} data-testid="activate-cycle-btn" className="bg-emerald-600 hover:bg-emerald-700">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <CheckCheck className="w-4 h-4 mr-2" />{t.activateCycle}
          </Button>
        </div>
      </div>
    );
  };

  // ============== NAV BUTTONS HELPER ==============
  const NavButtons = ({ onNext, nextDisabled }) => (
    <div className="flex justify-between pt-4">
      <Button variant="outline" onClick={goToPrevStep} disabled={currentStepIndex === 0}>
        <ChevronLeft className="w-4 h-4 mr-2" />{t.prev}
      </Button>
      <Button onClick={onNext || goToNextStep} disabled={saving || nextDisabled} data-testid={`step-${currentStepId}-next`}>
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {t.next} <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  // ============== STEP ROUTER ==============
  const renderStepContent = () => {
    if (!currentStep) return null;
    const renderers = { B1: renderB1, B2: renderB2, C1: renderC1, C2: renderC2, C3: renderC3,
      N1: renderN1, N2: renderN2, N3: renderN3, P1: renderP1, M1: renderM1, X: renderX };
    const renderer = renderers[currentStep.id];
    return renderer ? renderer() : <p>Unknown step: {currentStep.id}</p>;
  };

  // ============== COMPLETED STATE ==============
  const [editMode, setEditMode] = useState(false);

  if (onboardingComplete && !editMode) {
    return (
      <Card className="max-w-2xl mx-auto" data-testid="onboarding-complete">
        <CardContent className="pt-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{t.onboardingComplete}</h2>
          <p className="text-gray-600 mb-4">{t.onboardingCompleteDesc}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setEditMode(true); setOnboardingComplete(false); loadDynamicSteps(); }} data-testid="edit-onboarding-btn">
              <Settings2 className="w-4 h-4 mr-2" />{lang === 'es' ? 'Editar Configuracion' : 'Edit Configuration'}
            </Button>
            <Button onClick={onComplete} data-testid="go-to-dashboard-btn">{t.goToDashboard}<ChevronRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ============== LOADING STATE ==============
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (steps.length === 0) {
    return <div className="text-center py-12 text-gray-500">{lang === 'es' ? 'No se pudieron cargar los pasos' : 'Could not load steps'}</div>;
  }

  // ============== MAIN RENDER ==============
  const requiredCompleted = steps.filter(s => s.required && s.status === 'completed').length;
  const totalRequired = steps.filter(s => s.required).length;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="dynamic-onboarding">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <p className="text-gray-600 text-sm mt-1">
          {lang === 'es'
            ? `${steps.length} pasos para tu plan`
            : `${steps.length} steps for your plan`}
        </p>
        <Progress value={(requiredCompleted / totalRequired) * 100} className="mt-4 h-2" />
        <p className="text-sm text-gray-500 mt-1">{requiredCompleted} / {totalRequired} {t.requiredSteps}</p>
      </div>

      <StepIndicator steps={steps} currentStepIndex={currentStepIndex} t={t} />

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              {currentStep?.id === 'B1' ? <Building2 className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'B2' ? <Calendar className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'C1' ? <BarChart3 className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'C2' ? <ClipboardList className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'C3' ? <Users className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'N1' ? <FileText className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'N2' ? <Shield className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'N3' ? <Settings className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'P1' ? <Activity className="w-6 h-6 text-blue-600" /> :
               currentStep?.id === 'M1' ? <Settings2 className="w-6 h-6 text-blue-600" /> :
               <CheckCheck className="w-6 h-6 text-blue-600" />}
            </div>
            <div>
              <CardTitle className="text-lg">
                {t.step} {currentStepIndex + 1} {t.of} {steps.length}: {lang === 'es' ? currentStep?.title_es : currentStep?.title_en}
              </CardTitle>
              <CardDescription>{lang === 'es' ? currentStep?.desc_es : currentStep?.desc_en}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>
    </div>
  );
}
